import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import { Luggage } from "../../types/luggage";

interface SortConfig {
  key: keyof Luggage | '';
  direction: 'asc' | 'desc';
}

export default function Luggages() {
  const [luggages, setLuggages] = useState<Luggage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchLuggages();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  const fetchLuggages = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/luggages");
      const allLuggages = response.data;
      
      const filteredLuggages = allLuggages.filter((luggage: Luggage) => {
        const searchLower = searchTerm.toLowerCase();
        return luggage.description.toLowerCase().includes(searchLower);
      });

      if (sortConfig.key !== '') {
        filteredLuggages.sort((a: Luggage, b: Luggage) => {
          const aValue = String(a[sortConfig.key as keyof Luggage]);
          const bValue = String(b[sortConfig.key as keyof Luggage]);
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      const totalItems = filteredLuggages.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setTotalPages(calculatedTotalPages);
      setLuggages(filteredLuggages.slice(startIndex, endIndex));
    } catch (error) {
      setError("Failed to fetch luggages");
      console.error("Error fetching luggages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this luggage?")) {
      try {
        await axiosInstance.delete(`/luggages/${id}`);
        fetchLuggages();
      } catch (error) {
        setError("Failed to delete luggage");
        console.error("Error deleting luggage:", error);
      }
    }
  };

  const handleSort = (key: keyof Luggage) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: keyof Luggage) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? "rounded-lg bg-brand-500 text-white"
              : "rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Luggages"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/luggage/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add Luggage
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="p-4 border-b">
          <Input
            placeholder="Search luggages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="border-b border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('description')}
                >
                  Description {renderSortIcon('description')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : luggages.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                    No luggages found
                  </td>
                </tr>
              ) : (
                luggages.map((luggage) => (
                  <tr key={luggage.id}>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {luggage.description}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        luggage.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {luggage.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-end">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                        to={`/luggage/detail/${luggage.id}`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        View
                      </Link>
                      <Link
                        to={`/luggage/edit/${luggage.id}`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(luggage.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && luggages.length > 0 && (
          <div className="px-6 py-4 border-t">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 